---
title: Fetch and intake
description: Config lint, staging the night in, and the gates that refuse a bad one.
---

Before the GPU does anything, two cheap steps decide whether the night is
worth spending hours on: the configuration is linted, and the footage is
staged and gated.

## First, the config lint

`store.yml` is written by this app, not by hand, so the very first step of
every night validates it against a typed allowlist. Unknown sections, wrong
types and out-of-range values fail the run in seconds. A misspelled key
inside a known section is a warning with a did-you-mean suggestion.

The lint also knows a few rules a type check cannot express:

- Times must be zero-padded `HH:MM`.
- Kafka and MQTT addresses must be `host:port`, because the pipeline splits
  them into its own connection strings.
- The detector's image size must match its model size exactly. The wrong
  size does not error later; it feeds the engine wrong dimensions and
  produces garbage.
- Video extensions must be bare lowercase names like `mp4`. A dot or a
  capital letter would stage zero cameras and make it look like the
  recorder's fault.

## Then, stage the night in

The fetch stage copies one night from the object store to the machine's own
disk, into `sessions/<date>/`, mirroring the bucket layout exactly:

```
<bucket>/<store_id>/<date>/videos/<camera>.<ext>
<bucket>/<store_id>/<date>/camInfo/<camera>.yml
<bucket>/<store_id>/<date>/session.yml        (optional)
```

rclone owns the transport: parallel transfers, retries, atomic renames, and
support for almost any storage backend, which is why adding a new client's
storage is a connection string, not new code. The wrapper around it owns the
contract:

- **The camera-count gate runs before bytes move.** If the bucket does not
  hold the expected number of cameras, the night fails before a single
  download starts.
- **A completeness check runs after.** A partial copy fails the night rather
  than starving the pipeline mid-run.
- **Old nights are pruned.** Staged sessions beyond the keep window (three
  nights by default) are deleted to free disk.

Why stage to disk instead of streaming from the bucket: the video decoder
needs seekable local files, a run that dies mid-night restarts from disk
instead of re-downloading, and the staged folder is the immutable input you
re-run against when tuning weeks later.

## One camera, one file

Extensions are matched case-insensitively, so a recorder writing `.MP4`
still stages. But the same camera arriving in two containers, say
`cam-01.mp4` and `cam-01.mkv`, is refused outright: the camera count would
still look right while stages disagreed about which file to read. Every
stage that walks the videos folder shares one definition of what a camera
file is, so intake and perception can never drift apart on it.

## What breaks here

- **Empty listing.** The recorder's upload did not run (fix that job, not
  this one), or the site changed container format and the accepted
  extensions have not caught up.
- **Camera-count gate.** A camera is down, or the upload is still in
  flight.
- **Auth errors.** Credentials are missing from the machine's environment.
  They are never in configuration, by design.
